import { useState, useEffect } from 'react';
import { useReactFlow, Edge, Node } from '@xyflow/react';
import { Play, Loader2 } from 'lucide-react';
import { generateImageV2 } from '../../../../utils/api';
import { geminiConfigManager } from '../../../../utils/geminiConfig';

const GenImageContent = ({ data, id }: { data: any, id: string }) => {
  const { updateNodeData, getEdges, getNode, addNodes, addEdges, setEdges } = useReactFlow();
  const [isLoading, setIsLoading] = useState(false);

  const [configs, setConfigs] = useState({
    models: [] as string[],
    aspect_ratios: {} as Record<string, string[]>,
    image_sizes: {} as Record<string, string[]>,
    input_image_limits: {} as Record<string, number>
  });
  const [configsLoaded, setConfigsLoaded] = useState(false);

  const getCurrentModelConfigs = (model: string) => {
    return {
      aspect_ratios: configs.aspect_ratios[model] || [],
      image_sizes: configs.image_sizes[model] || [],
      input_image_limits: configs.input_image_limits[model] || 3
    };
  };

  useEffect(() => {
    const loadConfigs = async () => {
      try {
        const supportedConfigs = await geminiConfigManager.fetchConfigs();
        setConfigs({
          models: supportedConfigs.models,
          aspect_ratios: supportedConfigs.aspect_ratios,
          image_sizes: supportedConfigs.image_sizes,
          input_image_limits: supportedConfigs.input_image_limits
        });
        setConfigsLoaded(true);
      } catch (error) {
        console.error('Failed to load configs:', error);
        setConfigsLoaded(true);
      }
    };

    loadConfigs();

    const unsubscribe = geminiConfigManager.subscribe(() => {
      const supportedConfigs = geminiConfigManager.getConfigs();
      if (supportedConfigs) {
        setConfigs({
          models: supportedConfigs.models,
          aspect_ratios: supportedConfigs.aspect_ratios,
          image_sizes: supportedConfigs.image_sizes,
          input_image_limits: supportedConfigs.input_image_limits
        });
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!configsLoaded || !data.model) return;

    const modelConfigs = getCurrentModelConfigs(data.model);

    const currentAspectRatio = data.aspect_ratio;
    const validAspectRatios = modelConfigs.aspect_ratios;

    if (currentAspectRatio && !validAspectRatios.includes(currentAspectRatio)) {
      update('aspect_ratio', validAspectRatios[0]);
    }

    const currentImageSize = data.image_size;
    const validImageSizes = modelConfigs.image_sizes;

    if (currentImageSize && !validImageSizes.includes(currentImageSize)) {
      update('image_size', validImageSizes[0]);
    }

    const newMaxConnections = modelConfigs.input_image_limits;
    update('maxConnections', newMaxConnections);

    // 删除多余的 Ref Image 连接线
    const allEdges = getEdges();
    const existingRefImageEdges = allEdges.filter(
      e => e.target === id && e.targetHandle === 'in_imgs'
    );

    if (existingRefImageEdges.length > newMaxConnections) {
      const countToRemove = existingRefImageEdges.length - newMaxConnections;
      const edgesToRemove = existingRefImageEdges.slice(0, countToRemove);
      const idsToRemove = new Set(edgesToRemove.map(e => e.id));
      setEdges((eds) => eds.filter(e => !idsToRemove.has(e.id)));
    }
  }, [data.model, configsLoaded]);

  const update = (key: string, value: any) => {
    updateNodeData(id, { [key]: value });
  };

  /**
   * 核心逻辑：收集上游数据 -> 调用接口 -> 分发下游结果
   */
  const handleGenerate = async () => {
    if (isLoading) return;
    setIsLoading(true);

    const currentNode = getNode(id);
    if (!currentNode) return;

    // --- STEP 1: GATHER INPUTS (回溯上游节点) ---
    const allEdges = getEdges();
    const incomingEdges = allEdges.filter(edge => edge.target === id);

    let collectedPrompt = "";
    const collectedRefImages: string[] = [];

    incomingEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      if (!sourceNode) return;

      // 根据连接的 Handle ID 判断数据类型
      if (edge.targetHandle === 'in_text') {
        // 如果上游是文本节点，提取 text
        if (sourceNode.data?.text) {
          collectedPrompt += (sourceNode.data.text as string) + " ";
        }
      } else if (edge.targetHandle === 'in_imgs') {
        // 如果上游是图片节点，提取 url
        if (sourceNode.data?.url) {
          collectedRefImages.push(sourceNode.data.url as string);
        } else if (sourceNode.data?.imageUrl) {
          // 可能是上一步生成的图片结果
          collectedRefImages.push(sourceNode.data.imageUrl as string);
        }
      }
    });

    // --- STEP 2: PREPARE PAYLOAD (构建请求参数) ---
    const modelConfigs = getCurrentModelConfigs(data.model || configs.models[0]);
    const apiPayload: any = {
      prompt: collectedPrompt.trim() || "Default prompt (no input connected)",
      reference_images: collectedRefImages,
      aspect_ratio: data.aspect_ratio || modelConfigs.aspect_ratios[0],
      model: data.model || configs.models[0],
      negative_prompt: data.negative_prompt || undefined,
      template_id: data.template_id || undefined
    };

    // 只有当模型支持 image_size 时才添加该参数
    if (modelConfigs.image_sizes.length > 0) {
      apiPayload.image_size = data.image_size || modelConfigs.image_sizes[0];
    }

    console.log("📦 Sending Payload to Backend:", apiPayload);

    // --- STEP 3: PREPARE DOWNSTREAM NODES (准备下游节点) ---
    // 确保有足够的 Result 节点来接收结果
    const connectedOutputEdges = allEdges.filter(
      (edge) => edge.source === id && edge.sourceHandle === 'out_res'
    );
    const neededNodes = 1;
    let targetNodeIds: string[] = connectedOutputEdges.map(e => e.target);

    // 自动创建缺失的 Result 节点 (Auto-create missing nodes)
    if (targetNodeIds.length < neededNodes) {
      const missingCount = neededNodes - targetNodeIds.length;
      const newNodes: Node[] = [];
      const newEdges: Edge[] = [];

      // --- Dimension Calculation Logic ---
      // Max image side length 256px as requested.
      // We add approximate padding for Node Header (32px), Padding (24px), Footer (20px).
      // Total Vertical Chrome: ~80px.
      // Use a larger offset for the new nodes 
      const startX = currentNode.position.x + 350;

      // Adjust vertical spacing based on existing nodes
      const verticalGap = 20;
      let startY = currentNode.position.y + (targetNodeIds.length * (256 + verticalGap));

      for (let i = 0; i < missingCount; i++) {
        const newNodeId = `result-image-${Date.now()}-${i}`;
        targetNodeIds.push(newNodeId); // Add to our list of targets

        newNodes.push({
          id: newNodeId,
          type: 'result-image',
          position: { x: startX, y: startY },
          data: { label: `Result ${targetNodeIds.length}`, imageUrl: null }, // Init as empty
          style: { width: 256, height: 256 },
        });

        newEdges.push({
          id: `e-${id}-${newNodeId}`,
          source: id,
          sourceHandle: 'out_res',
          target: newNodeId,
          targetHandle: 'in_res',
          animated: true,
          style: { stroke: '#22d3ee', strokeWidth: 2 },
        });
        startY += (256 + verticalGap); // Increase vertical step for next node
      }
      addNodes(newNodes);
      addEdges(newEdges);
      // 等待 React Flow 更新 State，虽然这里我们已经有了 ID 列表
    }

    // --- STEP 4: UPDATE DOWNSTREAM NODES TO LOADING STATE ---        
    targetNodeIds.forEach((targetId) => {
      updateNodeData(targetId, {
        status: 'LOADING',
        imageUrl: null,
        timestamp: Date.now()
      });
    });

    // --- STEP 5: CALL API (调用接口) ---      
    try {
      const response = await generateImageV2(apiPayload);
      console.log('Full API response:', response);
      const mockImages = response.data.image_urls;
      console.log('Raw image_urls from API:', mockImages);

      // --- STEP 6: UPDATE DOWNSTREAM NODES WITH RESULTS (更新下游节点数据) ---        
      targetNodeIds.forEach((targetId, index) => {
        if (mockImages[index]) {
          const imageUrl = mockImages[index];
          console.log(`Updating node ${targetId} with image URL: ${imageUrl}`);
          updateNodeData(targetId, {
            status: 'COMPLETED',
            imageUrl: imageUrl,
            timestamp: Date.now()
          });
        } else {
          // No result for this node, set to error state
          updateNodeData(targetId, {
            status: 'ERROR',
            imageUrl: null,
            timestamp: Date.now()
          });
        }
      });

    } catch (error) {
      console.error("Generation failed", error);
      alert("Failed to generate images");

      // Update downstream nodes to error state
      targetNodeIds.forEach((targetId) => {
        updateNodeData(targetId, {
          status: 'ERROR',
          imageUrl: null,
          timestamp: Date.now()
        });
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-1">
      {!configsLoaded && (
        <div className="flex items-center justify-center py-2 text-xs text-slate-400">
          <Loader2 size={12} className="animate-spin mr-2" />
          Loading configurations...
        </div>
      )}

      {configsLoaded && (
        <>
          {/* Model - 单独一行 */}
          <div>
            <label className="text-[10px] text-slate-400 block mb-1">Model</label>
            <select
              className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs outline-none nodrag"
              value={data.model || configs.models[0]}
              onChange={(e) => update('model', e.target.value)}
            >
              {configs.models.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>

          {/* Aspect Ratio 和 Image Size - 两个一行 */}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[10px] text-slate-400 block mb-1">Aspect Ratio</label>
              <select
                className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs outline-none nodrag"
                value={data.aspect_ratio || getCurrentModelConfigs(data.model || configs.models[0]).aspect_ratios[0]}
                onChange={(e) => update('aspect_ratio', e.target.value)}
              >
                {getCurrentModelConfigs(data.model || configs.models[0]).aspect_ratios.map(ar => (
                  <option key={ar} value={ar}>{ar}</option>
                ))}
              </select>
            </div>
            {getCurrentModelConfigs(data.model || configs.models[0]).image_sizes.length > 0 && (
              <div className="flex-1">
                <label className="text-[10px] text-slate-400 block mb-1">Image Size</label>
                <select
                  className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs outline-none nodrag"
                  value={data.image_size || getCurrentModelConfigs(data.model || configs.models[0]).image_sizes[0]}
                  onChange={(e) => update('image_size', e.target.value)}
                >
                  {getCurrentModelConfigs(data.model || configs.models[0]).image_sizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </>
      )}

      {/* Generate Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleGenerate();
        }}
        disabled={isLoading || !configsLoaded}
        className={`nodrag w-full flex items-center justify-center gap-2 text-white text-xs font-bold py-2 px-2 rounded transition shadow-lg mt-1 active:scale-95 ${isLoading || !configsLoaded
          ? 'bg-slate-700 border border-slate-600 cursor-not-allowed text-slate-400'
          : 'bg-blue-600 hover:bg-blue-500 border border-blue-500 shadow-blue-900/20'
          }`}
      >
        {isLoading ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <Play size={14} fill="currentColor" />
        )}
        {isLoading ? 'GENERATING...' : 'GENERATE IMAGE'}
      </button>
    </div>
  );
};

export default GenImageContent;